import { AppError } from '@shared/api/error'
import { CoproductionSyncError } from '@entities/partnership'

export type CoproductionErrorField = 'email' | 'percentage'

export type CoproductionErrorFeedback = {
  title: string
  description: string
  field?: CoproductionErrorField
  fieldMessage?: string
}

const feedbackByCode: Record<string, CoproductionErrorFeedback> = {
  PartnershipRoleConflictError: {
    title: 'Produto salvo, mas a coprodução não foi adicionada',
    description:
      'Este usuário já é afiliado aprovado deste produto e não pode ser adicionado como coprodutor.',
    field: 'email',
    fieldMessage:
      'Este usuário já é afiliado aprovado deste produto e não pode ser adicionado como coprodutor.',
  },
  SelfCoproductionInviteError: {
    title: 'Produto salvo, mas revise a coprodução',
    description: 'Confira o e-mail do coprodutor antes de tentar novamente.',
    field: 'email',
    fieldMessage: 'O dono do produto não pode ser coprodutor do próprio produto.',
  },
  CoproductionUserNotRegisteredError: {
    title: 'Produto salvo, mas a coprodução não foi adicionada',
    description:
      'O e-mail informado não possui conta na VendePay. Peça para a pessoa se cadastrar antes de convidar.',
    field: 'email',
    fieldMessage:
      'Este e-mail não possui conta na plataforma. O coprodutor precisa se cadastrar antes do convite.',
  },
  PartnershipReceivingBlockedError: {
    title: 'Produto salvo, mas a coprodução não foi adicionada',
    description: 'Todos os participantes precisam ter o KYC aprovado para configurar a coprodução.',
    field: 'email',
    fieldMessage:
      'Todos os participantes precisam ter o KYC aprovado para configurar a coprodução.',
  },
  InvalidPartnershipPercentageError: {
    title: 'Produto salvo, mas revise a coprodução',
    description: 'Confira o percentual do coprodutor antes de tentar novamente.',
    field: 'percentage',
    fieldMessage: 'O percentual do coprodutor deve ficar entre 1% e 99%.',
  },
  CoproductionInviteAlreadyOpenError: {
    title: 'Produto salvo, mas já existe uma coprodução',
    description:
      'Este produto já possui um convite pendente ou um coprodutor ativo. Atualizamos os dados para você revisar.',
  },
  CoproductionInviteNotFoundError: {
    title: 'Produto salvo, mas a coprodução mudou',
    description:
      'O convite ou vínculo de coprodução não existe mais. Atualizamos os dados para você tentar novamente.',
  },
  CoproductionInviteUnavailableError: {
    title: 'Produto salvo, mas a coprodução mudou',
    description:
      'Este convite ou vínculo não está mais disponível para alteração. Atualizamos os dados para você revisar.',
  },
  PartnershipProductNotOwnedError: {
    title: 'Produto salvo, mas a coprodução não foi alterada',
    description: 'Você não tem permissão para gerenciar a coprodução deste produto.',
  },
  ProductNotFoundError: {
    title: 'Não foi possível concluir a operação',
    description: 'O produto não está mais disponível. Atualize a página e tente novamente.',
  },
  PartnershipUserNotFoundError: {
    title: 'Produto salvo, mas a coprodução não foi adicionada',
    description:
      'Não foi possível localizar os dados da sua conta para configurar a coprodução. Atualize a página e tente novamente.',
  },
}

function fallbackFeedback(error: unknown): CoproductionErrorFeedback {
  if (!(error instanceof AppError)) {
    return {
      title: 'Produto salvo, mas não foi possível confirmar a coprodução',
      description: 'Verifique sua conexão e tente adicionar o coprodutor novamente.',
    }
  }

  if (error.status === 401) {
    return {
      title: 'Sessão expirada',
      description: 'Entre novamente para continuar. As alterações do produto já foram salvas.',
    }
  }
  if (error.status === 429) {
    return {
      title: 'Produto salvo, mas a coprodução não foi adicionada',
      description:
        'Muitas tentativas foram realizadas. Aguarde alguns instantes e tente adicionar o coprodutor novamente.',
    }
  }
  if (error.status >= 500) {
    return {
      title: 'Produto salvo, mas a coprodução não foi concluída',
      description:
        'O serviço está temporariamente indisponível. Tente adicionar o coprodutor novamente em alguns instantes.',
    }
  }
  return {
    title: 'Produto salvo, mas não foi possível confirmar a coprodução',
    description: 'Atualizamos os dados da coprodução. Revise as informações e tente novamente.',
  }
}

export function coproductionErrorFeedback(error: unknown): CoproductionErrorFeedback | undefined {
  if (!(error instanceof CoproductionSyncError)) return undefined

  const underlying = error.underlyingError
  const base =
    underlying instanceof AppError
      ? (feedbackByCode[underlying.code] ?? fallbackFeedback(underlying))
      : fallbackFeedback(underlying)

  if (!error.previousRemoved) return base

  return {
    ...base,
    title: 'Produto salvo, mas o novo coprodutor não foi adicionado',
    description: `A coprodução anterior foi removida, mas não foi possível adicionar o novo coprodutor. ${base.description}`,
  }
}
